import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bot, Phone, Square } from "lucide-react";
import {
  ApiError,
  hybridApi,
  type HybridCall,
  type HybridCallEvent,
  type HybridCallRequest,
  type HybridPlan,
  type HybridPlanVersion,
  type HybridProvider,
  type HybridSnippet,
  type HybridTransition,
  snippetAudioUrl,
} from "@/lib/api";
import { dateTime } from "@/lib/format";
import { diffLines, foldDiff } from "@/lib/diff";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Tabs,
  Td,
  Textarea,
  Th,
  type Tone,
} from "@/components/ui";
import { useClientTable } from "@/lib/table";
import { KnowledgeTab } from "@/features/hybrid-knowledge";
import { HintDialog, PromptsTab } from "@/features/hybrid-prompts";
import { CompanyField, useCompanyUsers, useInitialCompany } from "@/features/operations";
import { useAuth } from "@/app/auth";

// --- the page --------------------------------------------------------------

// The hybrid robot is the daemon's second brain: it knows the box's prompts by
// their sound, reads every other one with systemone (the fast decision model,
// ~50 ms) against the provider's playbook and the catalogue of eventualities,
// and asks the language model (qwen) only where neither has an answer. Its page is the whole system's, like the
// script robot's — one trunk, one line pool — and does not follow the company
// switcher.
const hybridTabs = [
  { id: "calls", label: "Anrufe" },
  { id: "plans", label: "Ablaufpläne" },
  { id: "prompts", label: "Ansagen & Hinweise" },
  { id: "knowledge", label: "Grundwissen" },
];

export function HybridPage() {
  const [params, setParams] = useSearchParams();
  const tab = hybridTabs.some((t) => t.id === params.get("tab")) ? params.get("tab")! : "calls";
  const setTab = (id: string) => setParams(id === "calls" ? {} : { tab: id }, { replace: true });
  const [starting, setStarting] = useState(false);

  return (
    <>
      <PageHeader
        title="Hybrid-Roboter"
        description="Der Roboter erkennt bekannte Ansagen am Klang, liest jede andere live mit dem Schnellmodell (dem schnellen Entscheider) gegen das Playbook des Anbieters und lässt das Sprachmodell nur übernehmen, wo beide keine sichere Antwort haben."
        actions={
          tab === "calls" ? (
            <Button variant="primary" onClick={() => setStarting(true)}>
              <Phone className="size-4" aria-hidden /> Anruf starten
            </Button>
          ) : undefined
        }
      />

      <Tabs active={tab} onChange={setTab} tabs={hybridTabs} />

      {tab === "calls" && <HybridCallsConsole />}
      {tab === "plans" && <PlansTab />}
      {tab === "prompts" && <PromptsTab />}
      {tab === "knowledge" && <KnowledgeTab />}

      {starting && <StartHybridCallDialog onClose={() => setStarting(false)} />}
    </>
  );
}

// --- calls -----------------------------------------------------------------

function callTone(c: HybridCall): Tone {
  switch (c.state) {
    case "succeeded":
      return "success";
    case "failed":
      return "danger";
    case "running":
      return "warning";
    default:
      return "neutral";
  }
}

function callLabel(c: HybridCall): string {
  switch (c.state) {
    case "queued":
      return "wartet";
    case "running":
      return "läuft";
    case "succeeded":
      return "erledigt";
    case "failed":
      return "fehlgeschlagen";
    default:
      return "abgebrochen";
  }
}

const outcomeLabels: Record<string, string> = {
  success: "eingespielt",
  bad_pin: "PIN abgelehnt",
  locked: "Box gesperrt",
  login_failed: "Anmeldung fehlgeschlagen",
  password_not_set: "Erstanruf, Passwort fehlt",
  unknown_number: "Nummer unbekannt",
  no_mailbox: "keine Mobilbox",
  could_not_activate: "nicht aktiviert",
  recording_rejected: "Aufnahme abgelehnt",
  plan_failed: "Plan passte nicht",
  budget: "Budget erschöpft",
  loop: "Schleife",
  no_response: "keine Antwort",
  no_answer: "nicht abgehoben",
  disconnected: "Gegenseite aufgelegt",
  sip_not_registered: "SIP nicht registriert",
  cancelled: "abgebrochen",
  timeout: "Zeitlimit",
  asr_unavailable: "Spracherkennung fehlt",
  llm_unavailable: "Sprachmodell fehlt",
  bad_request: "Auftrag unvollständig",
  internal: "interner Fehler",
};

const changeLabels: Record<string, string> = {
  barge_in: "frühe Taste",
  head_measured: "Anfang vermessen",
  end_key_learned: "End-Taste gelernt",
  audio_learned: "Aufnahme gelernt",
  wording_added: "Wortlaut gelernt",
  choice_learned: "Wahl gelernt",
  snippets_merged: "Schnipsel zusammengelegt",
  branch_added: "Zweig neben dem alten angelegt",
};

const viaLabels: Record<string, string> = {
  s1: "Schnellmodell (alt)",
  audio: "Klang der ganzen Ansage",
  head: "Anfang der Aufnahme",
  wording: "gelernter Wortlaut",
  choice: "gelernte Wahl",
  tone: "Aufnahmeton der Box",
  partial: "Zwischenstand von Whisper",
  retry: "Wiederholung nach dem Ende der Ansage",
  timer: "Zeit",
  else: "Sonst-Zweig",
};

// Calls before the one flow carry the modes that existed then.
export const modeLabels: Record<string, string> = {
  hybrid: "Schnipsel · Schnellmodell · Sprachmodell",
  auto: "auto (alt)",
  auto_only: "nur Plan (alt)",
  assist_only: "Lernen (alt)",
  probe_keys: "Tasten-Test (alt)",
};

export function outcomeLabel(o: string): string {
  return outcomeLabels[o] ?? o;
}

function HybridCallsConsole() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["hybrid-calls"],
    queryFn: () => hybridApi.calls(100),
    // A call is over in a minute or two; refreshing while the list is open is
    // what makes the console usable.
    refetchInterval: 5_000,
  });

  const cancel = useMutation({
    mutationFn: (id: number) => hybridApi.cancelCall(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["hybrid-calls"] }),
  });

  const table = useClientTable(data, {
    initial: { key: "createdAt", dir: "desc" },
    pageSize: 25,
    accessors: {
      state: (c) => callLabel(c),
      durationMs: (c) => c.durationMs || null,
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="createdAt" first="desc">
              Zeitpunkt
            </SortableTh>
            <SortableTh table={table} sortKey="msisdn">
              Nummer
            </SortableTh>
            <SortableTh table={table} sortKey="userName">
              Person
            </SortableTh>
            <SortableTh table={table} sortKey="provider">
              Provider
            </SortableTh>
            <SortableTh table={table} sortKey="planVersion">
              Plan
            </SortableTh>
            <SortableTh table={table} sortKey="state">
              Zustand
            </SortableTh>
            <SortableTh table={table} sortKey="llmCalls" first="desc">
              KI-Eingriffe
            </SortableTh>
            <SortableTh table={table} sortKey="learnedVersion" first="desc">
              gelernt
            </SortableTh>
            <SortableTh table={table} sortKey="durationMs" first="desc">
              Dauer
            </SortableTh>
            <Th />
          </tr>
        </thead>
        <tbody>
          {table.rows.map((c) => (
            <tr key={c.id}>
              <Td className="whitespace-nowrap muted">{dateTime(c.createdAt)}</Td>
              <Td className="tabular-nums">{c.msisdn}</Td>
              <Td>
                {c.userName || "—"}
                {c.origin === "announcement" && (
                  <span className="ml-2">
                    <Badge>Ansage</Badge>
                  </span>
                )}
              </Td>
              <Td>
                {c.provider}
                {!c.snippets && (
                  <span className="ml-2">
                    <Badge tone="info">nur Schnellmodell</Badge>
                  </span>
                )}
              </Td>
              <Td className="tabular-nums">
                {c.planVersion ? `v${c.planVersion}` : "—"}
              </Td>
              <Td>
                <Badge tone={callTone(c)}>{callLabel(c)}</Badge>
                {c.outcome && c.outcome !== "success" && (
                  <span className="ml-2 text-xs muted" title={c.error}>
                    {outcomeLabel(c.outcome)}
                  </span>
                )}
              </Td>
              <Td className="tabular-nums">
                {c.llmCalls}
                {c.handoffs > 0 && (
                  <span className="ml-1 text-xs muted">
                    ({c.handoffs}× ans Sprachmodell)
                  </span>
                )}
              </Td>
              <Td>
                {c.learnedVersion ? (
                  <Link to={`/hybrid?tab=plans&provider=${encodeURIComponent(c.provider)}&version=${c.learnedVersion}`}>
                    <Badge tone="info">v{c.learnedVersion}</Badge>
                  </Link>
                ) : (
                  "—"
                )}
              </Td>
              <Td className="tabular-nums">
                {c.durationMs ? `${Math.round(c.durationMs / 1000)} s` : "—"}
              </Td>
              <Td className="text-right whitespace-nowrap">
                {(c.state === "queued" || c.state === "running") && (
                  <Button size="sm" onClick={() => cancel.mutate(c.id)} disabled={c.cancelRequested}>
                    {c.cancelRequested ? "wird beendet" : "Abbrechen"}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => navigate(`/hybrid/calls/${c.id}`)}>
                  Öffnen
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(data ?? []).length === 0 && <Empty title="Noch keine Anrufe" />}
      <TableFooter table={table} noun="Anrufe" />
      {cancel.error && (
        <div className="p-4">
          <ErrorNote>{(cancel.error as Error).message}</ErrorNote>
        </div>
      )}
    </Card>
  );
}

export function useProviders() {
  return useQuery({ queryKey: ["hybrid-providers"], queryFn: () => hybridApi.providers() });
}

function StartHybridCallDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const initialCompany = useInitialCompany();
  const [draft, setDraft] = useState<HybridCallRequest & { companyId: number; userId: number }>({
    userId: 0,
    companyId: initialCompany,
    msisdn: "",
    pin: "",
    provider: "",
    audioKey: "",
    note: "",
    snippets: "",
  });

  const users = useCompanyUsers(draft.companyId);
  const providers = useProviders();

  const start = useMutation({
    mutationFn: () => hybridApi.startCall(draft),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["hybrid-calls"] });
      onClose();
      navigate(`/hybrid/calls/${c.id}`);
    },
  });

  return (
    <Dialog open onClose={onClose} title="Anruf des Hybrid-Roboters starten">
      <div className="flex flex-col gap-4">
        <Notice>
          Der Anruf wird vom Worker geführt, nicht von diesem Fenster. Die PIN wird
          verschlüsselt hinterlegt und nach dem Anruf gelöscht. Was die Box sagt, wird
          live transkribiert und erscheint auf der Seite des Anrufs.
        </Notice>

        <CompanyField
          value={draft.companyId}
          onChange={(id) => setDraft({ ...draft, companyId: id, userId: 0 })}
        />

        <Field label="Person" hint="Nummer, Provider und PIN kommen dann von dort.">
          <Select
            value={draft.userId}
            onChange={(e) => setDraft({ ...draft, userId: Number(e.target.value) })}
          >
            <option value={0}>— freie Nummer —</option>
            {(users.data?.items ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.lastName}, {u.firstName} ({u.msisdn})
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Nummer" hint="Leer: die Nummer der gewählten Person.">
          <Input value={draft.msisdn} onChange={(e) => setDraft({ ...draft, msisdn: e.target.value })} />
        </Field>

        <Field label="Mobilbox-PIN" hint="Leer: die hinterlegte PIN der Person.">
          <Input
            type="password"
            autoComplete="off"
            value={draft.pin}
            onChange={(e) => setDraft({ ...draft, pin: e.target.value })}
          />
        </Field>

        <Field label="Provider" hint="Bestimmt den Ablaufplan. Leer: der Provider der Person.">
          <Select
            value={draft.provider}
            onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
          >
            <option value="">— der Provider der Person —</option>
            {(providers.data ?? []).map((p) => (
              <option key={p.key} value={p.key}>
                {p.displayName} ({p.key})
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Ansage" hint="Schlüssel einer gerenderten Ansage. Leer: die Standardansage wird gesprochen.">
          <Input
            value={draft.audioKey ?? ""}
            onChange={(e) => setDraft({ ...draft, audioKey: e.target.value })}
          />
        </Field>

        <Field label="Notiz">
          <Input value={draft.note ?? ""} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
        </Field>

        <Field
          label="Erkennung"
          hint="Für Tests: zwingt diesen Anruf auf einen Weg. Leer gilt die globale Einstellung unter Ablaufpläne."
        >
          <Select
            value={draft.snippets ?? ""}
            onChange={(e) => setDraft({ ...draft, snippets: e.target.value as HybridCallRequest["snippets"] })}
          >
            <option value="">— wie global eingestellt —</option>
            <option value="on">zuerst Schnipsel (am Klang), dann Wortlaut, dann Sprachmodell</option>
            <option value="off">ohne Klang: nur Wortlaut und Sprachmodell</option>
          </Select>
        </Field>

        <Notice>
          Jeder Anruf läuft gleich: bekannte Ansagen erkennt der Roboter am Klang, jede andere
          liest das Schnellmodell live gegen das Playbook; das Sprachmodell übernimmt nur, wo beide keine sichere
          Antwort haben. Ohne Plan steuert das Sprachmodell jeden Schritt, und der Anruf hinterlässt die
          erste Version: die Schnipsel mit Aufnahme und mit dem Text, nach dem das Schnellmodell sie liest.
        </Notice>

        {start.error && <ErrorNote>{(start.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate()}>
            Anrufen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- one call, live --------------------------------------------------------

function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

type Payload = Record<string, unknown>;

function str(p: Payload, k: string): string {
  const v = p[k];
  return v === undefined || v === null ? "" : String(v);
}

function num(p: Payload, k: string): number {
  const v = p[k];
  return typeof v === "number" ? v : 0;
}

function obj(p: Payload, k: string): Payload | undefined {
  const v = p[k];
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Payload) : undefined;
}

function mergeEvents(prev: HybridCallEvent[], more: HybridCallEvent[]): HybridCallEvent[] {
  // A double delivery must not become a double row.
  const bySeq = new Map<number, HybridCallEvent>();
  for (const e of prev) bySeq.set(e.seq, e);
  for (const e of more) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export function HybridCallPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const qc = useQueryClient();

  const { data: call, isLoading } = useQuery({
    queryKey: ["hybrid-call", id],
    queryFn: () => hybridApi.call(id),
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === "queued" || s === "running" ? 3_000 : false;
    },
    enabled: Number.isFinite(id) && id > 0,
  });
  const running = call?.state === "queued" || call?.state === "running";

  const [events, setEvents] = useState<HybridCallEvent[]>([]);
  const seqRef = useRef(0);
  useEffect(() => {
    if (!Number.isFinite(id) || id <= 0) return;
    let stopped = false;
    const load = async () => {
      try {
        const more = await hybridApi.events(id, seqRef.current);
        if (stopped || more.length === 0) return;
        seqRef.current = Math.max(seqRef.current, ...more.map((e) => e.seq));
        setEvents((prev) => mergeEvents(prev, more));
      } catch {
        // The next tick tries again; a transient failure is not worth a banner.
      }
    };
    void load();
    if (!running) {
      return () => {
        stopped = true;
      };
    }
    const t = setInterval(load, 1_500);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [id, running]);

  const cancel = useMutation({
    mutationFn: () => hybridApi.cancelCall(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["hybrid-call", id] }),
  });
  const { me } = useAuth();
  const [hintFor, setHintFor] = useState<{ transcript: string; useq: number } | null>(null);

  const talkRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!running) return;
    for (const r of [talkRef, stepsRef]) {
      if (r.current) r.current.scrollTop = r.current.scrollHeight;
    }
  }, [events, running]);

  const talk = events.filter((e) => e.kind === "utterance" || e.kind === "tone" || e.kind === "line");
  // What qwen was told about the provider on this call, after its general knowledge.
  const hints = str(events.find((e) => e.kind === "session")?.payload ?? {}, "hints");
  const hintLines = hints.split("\n").filter((l) => l.startsWith("- ")).length;
  const steps = events.filter(
    (e) => e.kind === "step" || e.kind === "action" || e.kind === "warning" || e.kind === "learned" || e.kind === "error" || e.kind === "done",
  );

  if (isLoading || !call) return <Spinner />;

  return (
    <>
      <PageHeader
        title={`Anruf #${call.id} · ${call.provider} · ${call.msisdn}`}
        description={[
          call.userName && `${call.userName}${call.company ? ` (${call.company})` : ""}`,
          call.origin === "announcement" ? "aus einer Ansage" : "von der Konsole",
          modeLabels[call.mode],
          call.note,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <>
            <Link to="/hybrid">
              <Button>Zur Liste</Button>
            </Link>
            {running && (
              <Button onClick={() => cancel.mutate()} disabled={cancel.isPending || call.cancelRequested}>
                <Square className="size-4" aria-hidden />{" "}
                {call.cancelRequested ? "wird beendet" : "Abbrechen"}
              </Button>
            )}
          </>
        }
      />

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge tone={callTone(call)}>{callLabel(call)}</Badge>
          <Badge tone={call.snippets ? "neutral" : "info"}>
            {call.snippets ? "Schnipsel zuerst, dann Schnellmodell" : "nur Schnellmodell (keine Schnipsel)"}
          </Badge>
          {call.outcome && (
            <Badge tone={call.outcome === "success" ? "success" : "danger"}>
              {outcomeLabel(call.outcome)}
              {call.code !== null && call.code !== undefined ? ` (${call.code})` : ""}
            </Badge>
          )}
          {call.planVersion > 0 && (
            <Link
              to={`/hybrid?tab=plans&provider=${encodeURIComponent(call.provider)}&version=${call.planVersion}`}
              className="hover:underline"
            >
              Plan v{call.planVersion}
            </Link>
          )}
          {call.learnedVersion > 0 && (
            <Link
              to={`/hybrid?tab=plans&provider=${encodeURIComponent(call.provider)}&version=${call.learnedVersion}`}
            >
              <Badge tone="info">gelernt → v{call.learnedVersion}</Badge>
            </Link>
          )}
          <span className="muted">
            {call.steps} Schritte · {call.llmCalls}× Sprachmodell · {call.handoffs}× ans Sprachmodell übergeben
          </span>
          {call.durationMs > 0 && <span className="muted">{Math.round(call.durationMs / 1000)} s</span>}
          {call.sessionId && <span className="muted tabular-nums">{call.sessionId}</span>}
          <Link to={`/hybrid?tab=prompts&provider=${encodeURIComponent(call.provider)}`} className="hover:underline">
            {hintLines > 0 ? `qwen hatte ${hintLines} ${hintLines === 1 ? "Hinweis" : "Hinweise"}` : "keine Hinweise für qwen"}
          </Link>
        </div>
        {hints && (
          <details className="mt-2 text-sm">
            <summary className="cursor-pointer muted">Was qwen über diesen Anbieter las</summary>
            <pre className="mt-1 overflow-x-auto text-xs leading-relaxed whitespace-pre-wrap">{hints}</pre>
          </details>
        )}
        {call.error && (
          <div className="mt-2">
            <ErrorNote>{call.error}</ErrorNote>
          </div>
        )}
        {call.summary && <p className="mt-2 text-sm">{call.summary}</p>}
        {cancel.error && (
          <div className="mt-2">
            <ErrorNote>{(cancel.error as Error).message}</ErrorNote>
          </div>
        )}
        {call.sessionId && (
          <div className="mt-3">
            <AudioPlayer
              src={
                call.live
                  ? `/api/media/calls/${call.sessionId}/live.mp3`
                  : `/api/media/calls/${call.sessionId}/recording.mp3`
              }
              label={call.live ? "läuft gerade — Mithören" : "Mitschnitt"}
            />
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
            <h2 className="text-base font-semibold">Gespräch</h2>
            <p className="text-sm muted">Was die Box gesagt hat, live transkribiert.</p>
          </div>
          <div ref={talkRef} className="max-h-[70vh] overflow-y-auto">
            {talk.length === 0 ? (
              <Empty title={running ? "Noch nichts gehört" : "Nichts aufgezeichnet"} />
            ) : (
              <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                {talk.map((e) => (
                  <li key={e.seq} className="px-4 py-2">
                    <TalkRow
                      e={e}
                      onHint={
                        me?.role === "global_admin"
                          ? (transcript, useq) => setHintFor({ transcript, useq })
                          : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <div className="border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
            <h2 className="text-base font-semibold">Schritte</h2>
            <p className="text-sm muted">
              Was der Plan erkannt, das Modell entschieden und der Roboter getan hat.
            </p>
          </div>
          <div ref={stepsRef} className="max-h-[70vh] overflow-y-auto">
            {steps.length === 0 ? (
              <Empty title={running ? "Noch kein Schritt" : "Keine Schritte"} />
            ) : (
              <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                {steps.map((e) => (
                  <li key={e.seq} className="px-4 py-2">
                    <StepRow e={e} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      {hintFor && (
        <HintDialog
          provider={call.provider}
          seed={{ kind: "prompt", transcript: hintFor.transcript, callId: call.id, session: call.sessionId, useq: hintFor.useq }}
          onClose={() => setHintFor(null)}
        />
      )}
    </>
  );
}

function TalkRow({ e, onHint }: { e: HybridCallEvent; onHint?: (transcript: string, useq: number) => void }) {
  const p = e.payload ?? {};
  if (e.kind === "line") {
    const status = str(p, "status");
    const labels: Record<string, string> = {
      dialling: "wählt",
      connected: "verbunden",
      hungup_far: "Gegenseite hat aufgelegt",
      hungup_near: "aufgelegt",
    };
    return (
      <div className="flex items-baseline gap-3 text-sm">
        <span className="w-12 shrink-0 text-xs muted tabular-nums">{mmss(e.atMs)}</span>
        <Badge>{labels[status] ?? status}</Badge>
        {str(p, "detail") && <span className="text-xs muted">{str(p, "detail")}</span>}
      </div>
    );
  }
  if (e.kind === "tone") {
    return (
      <div className="flex items-baseline gap-3 text-sm">
        <span className="w-12 shrink-0 text-xs muted tabular-nums">{mmss(num(p, "at_ms") || e.atMs)}</span>
        <Badge tone={str(p, "kind") === "beep" ? "info" : "neutral"}>
          {str(p, "kind") === "beep" ? "Aufnahmeton" : "Ton"} {Math.round(num(p, "hz"))} Hz
        </Badge>
        <span className="text-xs muted">{num(p, "dur_ms")} ms</span>
      </div>
    );
  }
  const f = obj(p, "features") ?? {};
  const transcript = str(p, "transcript");
  const chips: { label: string; tone: Tone }[] = [];
  if (f.silent) chips.push({ label: "still", tone: "neutral" });
  if (f.fragment) chips.push({ label: "Fragment", tone: "warning" });
  if (f.garbled) chips.push({ label: "verstümmelt", tone: "warning" });
  if (f.hit_cap) chips.push({ label: "redet noch", tone: "warning" });
  for (const t of (Array.isArray(f.tones) ? f.tones : []) as Record<string, unknown>[]) {
    const beep = str(t, "kind") === "beep";
    chips.push({ label: `${beep ? "Aufnahmeton" : "Ton"} ${Math.round(num(t, "hz"))} Hz`, tone: beep ? "info" : "neutral" });
  }
  if (f.cut_by_action) chips.push({ label: "vor eigener Aktion", tone: "neutral" });
  if (f.sparse) chips.push({ label: "unvollständig transkribiert", tone: "warning" });
  if (str(p, "asr_error")) chips.push({ label: "Transkription fehlgeschlagen", tone: "danger" });
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="w-12 shrink-0 pt-0.5 text-xs muted tabular-nums">
        {mmss(num(p, "start_ms") || e.atMs)}
      </span>
      <div className="min-w-0 flex-1">
        <p className={transcript ? "" : "muted italic"} title={str(p, "transcript_raw") || undefined}>
          {transcript ||
            (str(p, "head")
              ? `am Anfang erkannt: ${str(p, "head")} (nicht transkribiert)`
              : f.silent
                ? "(Stille)"
                : "(kein Transkript)")}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs muted">
          <span>#{num(p, "useq")}</span>
          <span>· {((num(f, "duration_ms") || num(p, "end_ms") - num(p, "start_ms")) / 1000).toFixed(1)} s</span>
          {num(p, "asr_ms") > 0 && <span>· Whisper {num(p, "asr_ms")} ms</span>}
          {chips.map((c) => (
            <Badge key={c.label} tone={c.tone}>
              {c.label}
            </Badge>
          ))}
          {onHint && transcript && !f.partial && (
            <button
              type="button"
              className="ml-auto underline decoration-dotted underline-offset-2 hover:decoration-solid"
              onClick={() => onHint(transcript, num(p, "useq"))}
            >
              Hinweis für qwen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const sourceLabels: Record<string, string> = {
  plan: "Plan",
  s1: "Schnellmodell",
  llm: "Sprachmodell",
  handoff: "ans Sprachmodell",
  guard: "Schutz",
  timer: "Timer",
  rule: "Regel (alt)",
};

export const sourceTones: Record<string, Tone> = {
  plan: "success",
  s1: "info",
  llm: "warning",
  handoff: "warning",
  guard: "danger",
  timer: "neutral",
  rule: "neutral",
};

const actionLabels: Record<string, string> = {
  keys: "Tasten",
  wait: "warten",
  wait_beep: "auf Signalton warten",
  settle: "ausklingen lassen",
  play_greeting: "Ansage aufsprechen",
  hangup: "auflegen",
  done: "fertig",
  abort: "abbrechen",
};

function StepRow({ e }: { e: HybridCallEvent }) {
  const p = e.payload ?? {};
  const time = <span className="w-12 shrink-0 pt-0.5 text-xs muted tabular-nums">{mmss(e.atMs)}</span>;

  if (e.kind === "action") {
    const kind = str(p, "kind");
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <div className="min-w-0 flex-1 muted">
          <span>{actionLabels[kind] ?? kind}</span>
          {str(p, "keys") && <span className="ml-2 font-mono">{str(p, "keys")}</span>}
          {str(p, "detail") && <span className="ml-2 text-xs">{str(p, "detail")}</span>}
          {p.ok === false && (
            <span className="ml-2">
              <Badge tone="danger">fehlgeschlagen</Badge>
            </span>
          )}
          {num(p, "ms") > 0 && <span className="ml-2 text-xs">{num(p, "ms")} ms</span>}
        </div>
      </div>
    );
  }
  if (e.kind === "warning") {
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <Notice tone="warning">{str(p, "text")}</Notice>
      </div>
    );
  }
  if (e.kind === "error") {
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <ErrorNote>{str(p, "error") || str(p, "message") || "Fehler"}</ErrorNote>
      </div>
    );
  }
  if (e.kind === "learned") {
    const changes = Array.isArray(p.changes) ? (p.changes as Payload[]) : [];
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <Notice tone="success">
          <div className="font-medium">Der Plan hat dazugelernt</div>
          <ul className="mt-1 list-disc pl-4 text-xs">
            {changes.map((c, i) => (
              <li key={i}>
                {changeLabels[str(c, "kind")] ?? str(c, "kind")}
                {str(c, "id") && <span className="font-mono"> {str(c, "id")}</span>}
                {str(c, "detail") && <span> — {str(c, "detail")}</span>}
              </li>
            ))}
          </ul>
        </Notice>
      </div>
    );
  }
  if (e.kind === "done") {
    const r = obj(p, "result") ?? {};
    const outcome = str(r, "outcome");
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <Notice tone={outcome === "success" ? "success" : "warning"}>
          <span className="font-medium">{outcomeLabel(outcome)}</span>
          {str(r, "message") && <span> — {str(r, "message")}</span>}
          <span className="ml-2 text-xs">
            Exit {num(r, "exitcode")} · {num(r, "steps")} Schritte · {num(r, "llm_calls")} Modellaufrufe
          </span>
        </Notice>
      </div>
    );
  }

  // The label and reason of a qwen decision that was carried out as soon
  // as qwen had written its action.
  if (p.completes === true) {
    const d = obj(p, "decision");
    return (
      <div className="flex items-start gap-3 text-sm">
        {time}
        <div className="min-w-0 flex-1 text-xs muted">
          Begründung des Sprachmodells zu #{num(p, "useq")} ({num(p, "llm_ms")} ms)
          {d && str(d, "label") && <span className="ml-1 font-mono">{str(d, "label")}</span>}
          {d && str(d, "reason") && <div className="mt-0.5">{str(d, "reason")}</div>}
        </div>
      </div>
    );
  }

  // A step: what was heard was recognised by the plan (by its sound or by
  // systemone), decided by systemone's catalogue, by qwen or by a guard.
  const source = str(p, "source");
  const mode = str(p, "mode");
  const matched = obj(p, "matched");
  const decision = obj(p, "decision");
  const read = obj(p, "read");
  return (
    <div
      className={
        "flex items-start gap-3 text-sm " +
        (mode === "assist" ? "rounded-md bg-amber-50/60 px-2 py-1 dark:bg-amber-500/10" : "")
      }
    >
      {time}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={sourceTones[source] ?? "neutral"}>{sourceLabels[source] ?? source}</Badge>
          {mode === "assist" && <Badge tone="warning">außerhalb des Plans</Badge>}
          {p.provisional === true && <Badge tone="info">vorläufig, noch während der Ansage</Badge>}
          {p.resync === true && <Badge tone="success">zurück im Plan</Badge>}
          {p.early === true && <Badge tone="info">Taste während der Ansage</Badge>}
          {str(p, "state") && (
            <span className="font-mono text-xs">
              {str(p, "state")}
              {str(p, "next") && ` → ${str(p, "next")}`}
            </span>
          )}
          {str(p, "outcome") && <Badge tone="info">{outcomeLabel(str(p, "outcome"))}</Badge>}
          {num(p, "useq") > 0 && <span className="text-xs muted">nach #{num(p, "useq")}</span>}
        </div>
        {matched && (
          <div className="mt-1 text-xs muted">
            Schnipsel <span className="font-mono">{str(matched, "snippet")}</span> ·{" "}
            {(num(matched, "score") * 100).toFixed(0)} % über {viaLabels[str(matched, "via")] ?? str(matched, "via")}
          </div>
        )}
        {read && (
          <div className="mt-1 text-xs muted">
            Schnellmodell ({num(read, "ms")} ms): {str(read, "meaning")} {(num(read, "p") * 100).toFixed(0)} %
            {read.sure === true ? " · sicher" : " · unsicher"}
            {str(read, "snippet") && (
              <>
                {" "}· bekannt als <span className="font-mono">{str(read, "snippet")}</span>
              </>
            )}
            {str(read, "key") && (
              <>
                {" "}· Taste <span className="font-mono">{str(read, "key")}</span> für „{str(read, "clause")}“{" "}
                {(num(read, "option_p") * 100).toFixed(0)} %{read.option_sure === true ? "" : " (unsicher)"}
              </>
            )}
          </div>
        )}
        {str(p, "handoff_reason") && (
          <div className="mt-1 text-xs muted">Übergabe ans Sprachmodell: {handoffReason(str(p, "handoff_reason"))}</div>
        )}
        {decision && (
          <div className="mt-1 text-xs">
            <span className="font-medium">{actionLabels[str(decision, "action")] ?? str(decision, "action")}</span>
            {str(decision, "keys") && <span className="ml-1 font-mono">{str(decision, "keys")}</span>}
            {str(decision, "meaning") && <span className="ml-2 muted">{str(decision, "meaning")}</span>}
            {str(decision, "label") && <span className="ml-1 font-mono muted">{str(decision, "label")}</span>}
            {decision.confidence !== undefined && (
              <Badge tone={num(decision, "confidence") >= 0.8 ? "success" : num(decision, "confidence") >= 0.4 ? "warning" : "danger"}>
                {(num(decision, "confidence") * 100).toFixed(0)} %
              </Badge>
            )}
            {str(decision, "resume_state") && (
              <span className="ml-2 muted">
                weiter bei <span className="font-mono">{str(decision, "resume_state")}</span>
              </span>
            )}
            {decision.recording_rejected === true && (
              <span className="ml-2">
                <Badge tone="danger">Aufnahme abgelehnt</Badge>
              </span>
            )}
            {str(decision, "quote") && <div className="mt-0.5 muted">Zitat der Box: „{str(decision, "quote")}“</div>}
            {str(decision, "reason") && <div className="mt-0.5 muted">{str(decision, "reason")}</div>}
            {num(p, "llm_ms") > 0 && <span className="muted"> · Sprachmodell {num(p, "llm_ms")} ms</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// --- plans -----------------------------------------------------------------

const sourceLabel: Record<string, string> = {
  seed: "Startplan",
  manual: "von Hand",
  learned: "gelernt",
};

const sourceTone: Record<string, Tone> = {
  seed: "neutral",
  manual: "info",
  learned: "success",
};

function PlansTab() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const providers = useProviders();

  const providerParam = params.get("provider") ?? "";
  const provider = useMemo(() => {
    const list = providers.data ?? [];
    if (list.some((p) => p.key === providerParam)) return providerParam;
    return list[0]?.key ?? "";
  }, [providers.data, providerParam]);
  const setProvider = (key: string) =>
    setParams({ tab: "plans", provider: key }, { replace: true });

  const versionParam = Number(params.get("version") ?? 0);
  const [selectedVersion, setSelectedVersion] = useState(0);
  useEffect(() => {
    setSelectedVersion(versionParam || 0);
  }, [versionParam, provider]);

  const versions = useQuery({
    queryKey: ["hybrid-plans", provider],
    queryFn: () => hybridApi.versions(provider),
    enabled: provider !== "",
  });
  const active = versions.data?.find((v) => v.active);
  const shown = selectedVersion || active?.version || 0;

  const plan = useQuery({
    queryKey: ["hybrid-plan", provider, shown],
    queryFn: () => hybridApi.version(provider, shown),
    enabled: provider !== "" && shown > 0,
  });

  const [confirm, setConfirm] = useState<HybridPlanVersion | null>(null);
  const activate = useMutation({
    mutationFn: (v: HybridPlanVersion) => hybridApi.activate(provider, v.version),
    onSuccess: () => {
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ["hybrid-plans", provider] });
      void qc.invalidateQueries({ queryKey: ["hybrid-providers"] });
    },
  });

  const [forgetting, setForgetting] = useState(false);
  const forget = useMutation({
    mutationFn: () => hybridApi.forget(provider),
    onSuccess: () => {
      setForgetting(false);
      setSelectedVersion(0);
      void qc.invalidateQueries({ queryKey: ["hybrid-plans", provider] });
      void qc.invalidateQueries({ queryKey: ["hybrid-providers"] });
    },
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const selected = (providers.data ?? []).find((p) => p.key === provider);
  const [editorSeed, setEditorSeed] = useState<{ version: number; text: string } | null>(null);
  const [snippet, setSnippet] = useState<string | null>(null);

  const table = useClientTable(versions.data, {
    initial: { key: "version", dir: "desc" },
    pageSize: 25,
  });

  if (providers.isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <RobotSettingsCard canEdit={me?.role === "global_admin"} />
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Field label="Provider" hint="Ein Plan je Provider; die Nummer sagt nicht, wer die Box betreibt.">
            <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {(providers.data ?? []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.displayName} ({p.key}) — {p.serviceNumber || "Direktwahl"} — v
                  {p.activeVersion || "?"} aktiv, {p.versions} Versionen
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {me?.role === "global_admin" && (
          <>
            <Button onClick={() => setCreating(true)}>Neuer Provider</Button>
            {selected && <Button onClick={() => setEditing(true)}>Bearbeiten</Button>}
            {selected && selected.activeVersion > 0 && (
              <Button variant="danger" onClick={() => setForgetting(true)}>
                Neu lernen
              </Button>
            )}
          </>
        )}
      </div>

      {selected && selected.activeVersion === 0 && (
        <Notice>
          Noch kein Plan: der erste Anruf erkundet die Box — das Sprachmodell entscheidet jeden Schritt,
          das Schnellmodell liest mit — und der erfolgreiche Anruf wird {selected.versions > 0 ? `Version ${selected.versions + 1}` : "Version 1"}.
        </Notice>
      )}

      {(providers.data ?? []).length === 0 && (
        <Empty title="Noch kein Provider" hint="Ein Provider ist der Schlüssel, unter dem ein Ablaufplan liegt." />
      )}

      {provider && (
        <>
          <Card>
            <div className="border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-base font-semibold">Versionen</h2>
              <p className="text-sm muted">
                Jede gelernte Version wird sofort aktiv. Eine ältere zu aktivieren rollt den Plan
                zurück; die gelernte bleibt zum Nachlesen.
              </p>
            </div>
            {versions.isLoading ? (
              <Spinner />
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <SortableTh table={table} sortKey="version" first="desc">
                        Version
                      </SortableTh>
                      <SortableTh table={table} sortKey="source">
                        Quelle
                      </SortableTh>
                      <Th>Herkunft</Th>
                      <Th>Notiz</Th>
                      <SortableTh table={table} sortKey="createdAt" first="desc">
                        Erstellt
                      </SortableTh>
                      <Th>Aktiv</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((v) => (
                      <tr key={v.id} className={v.version === shown ? "bg-[var(--surface-sunken)]" : undefined}>
                        <Td className="tabular-nums font-medium">v{v.version}</Td>
                        <Td>
                          <Badge tone={sourceTone[v.source] ?? "neutral"}>{sourceLabel[v.source] ?? v.source}</Badge>
                        </Td>
                        <Td className="text-xs">
                          {v.learnedFromCallId ? (
                            <Link to={`/hybrid/calls/${v.learnedFromCallId}`} className="hover:underline">
                              Anruf #{v.learnedFromCallId}
                              {v.learnedFromSession && <span className="muted"> · {v.learnedFromSession}</span>}
                            </Link>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </Td>
                        <Td className="max-w-md truncate text-xs" title={v.note}>
                          {v.note || "—"}
                        </Td>
                        <Td className="whitespace-nowrap muted">{dateTime(v.createdAt)}</Td>
                        <Td>{v.active && <Badge tone="success">aktiv</Badge>}</Td>
                        <Td className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedVersion(v.version)}>
                            Ansehen
                          </Button>
                          {!v.active && (
                            <Button size="sm" onClick={() => setConfirm(v)}>
                              {active && v.version < active.version ? "Zurückrollen" : "Aktivieren"}
                            </Button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {(versions.data ?? []).length === 0 && (
                  <Empty title="Noch keine Version" hint="Beim ersten Anruf wird der Startplan des Providers angelegt, oder du speicherst unten einen." />
                )}
                <TableFooter table={table} noun="Versionen" />
              </>
            )}
          </Card>

          {shown > 0 && (
            <>
              <PlanView
                provider={provider}
                version={plan.data}
                loading={plan.isLoading}
                previous={versions.data?.filter((v) => v.version < shown).sort((a, b) => b.version - a.version)[0]?.version ?? 0}
                onEdit={(text) => setEditorSeed({ version: shown, text })}
                onSnippet={setSnippet}
              />
              {plan.data?.plan && <SnippetList plan={plan.data.plan} onSnippet={setSnippet} />}
              {plan.data?.plan && snippet && (
                <SnippetDialog plan={plan.data.plan} id={snippet} onClose={() => setSnippet(null)} />
              )}
            </>
          )}

          <PlanEditor
            provider={provider}
            seed={editorSeed}
            onSaved={(v) => {
              void qc.invalidateQueries({ queryKey: ["hybrid-plans", provider] });
              void qc.invalidateQueries({ queryKey: ["hybrid-providers"] });
              setSelectedVersion(v.version);
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm && active && confirm.version < active.version ? "Plan zurückrollen" : "Version aktivieren"}
        message={
          confirm ? (
            <>
              Version <b>v{confirm.version}</b> ({sourceLabel[confirm.source] ?? confirm.source}) wird die
              aktive Version für <b>{provider}</b>; die bisher aktive v{active?.version ?? "?"} bleibt
              erhalten. Der nächste Anruf folgt dann diesem Plan.
            </>
          ) : null
        }
        confirmLabel="Aktivieren"
        error={activate.error ? (activate.error as Error).message : null}
        busy={activate.isPending}
        onConfirm={() => confirm && activate.mutate(confirm)}
        onClose={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={forgetting}
        title="Plan vergessen und neu lernen"
        message={
          <>
            Alle Versionen von <b>{provider}</b> werden deaktiviert. Der nächste Anruf erkundet die Box
            von null — das Sprachmodell steuert jeden Schritt, ohne Plan und ohne Schnipsel — und der
            erfolgreiche Anruf wird die nächste Version. Die bisherigen Versionen bleiben als Historie und lassen sich wieder aktivieren.
          </>
        }
        confirmLabel="Neu lernen"
        challenge={provider}
        challengeLabel="Zur Bestätigung den Provider-Schlüssel eingeben"
        error={forget.error ? (forget.error as Error).message : null}
        busy={forget.isPending}
        onConfirm={() => forget.mutate()}
        onClose={() => setForgetting(false)}
      />

      {creating && (
        <ProviderDialog
          onClose={() => setCreating(false)}
          onSaved={(p) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ["hybrid-providers"] });
            setProvider(p.key);
          }}
        />
      )}
      {editing && selected && (
        <ProviderDialog
          existing={selected}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void qc.invalidateQueries({ queryKey: ["hybrid-providers"] });
          }}
        />
      )}
    </div>
  );
}

/** "0677=60, 0664=77" → {"0677":"60","0664":"77"}; malformed pairs are dropped. */
function parseDialIn(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of text.split(/[,;\s]+/)) {
    const [prefix, digits] = part.split("=");
    if (prefix && digits && /^\d+$/.test(prefix) && /^\d+$/.test(digits)) out[prefix] = digits;
  }
  return out;
}

function formatDialIn(dialIn: Record<string, string> | undefined): string {
  return Object.entries(dialIn ?? {})
    .map(([prefix, digits]) => `${prefix}=${digits}`)
    .join(", ");
}

function ProviderDialog({
  existing,
  onClose,
  onSaved,
}: {
  existing?: HybridProvider;
  onClose: () => void;
  onSaved: (p: HybridProvider) => void;
}) {
  const [draft, setDraft] = useState({
    key: existing?.key ?? "",
    displayName: existing?.displayName ?? "",
    serviceNumber: existing?.serviceNumber ?? "",
    dialIn: formatDialIn(existing?.dialIn),
    pinRaw: existing?.pinRaw ?? false,
    lockoutRisk: existing?.lockoutRisk ?? true,
    implicitSave: existing?.implicitSave ?? false,
  });
  const dialIn = parseDialIn(draft.dialIn);
  const noNumber = draft.serviceNumber.trim() === "" && Object.keys(dialIn).length === 0;
  const body = {
    key: draft.key,
    displayName: draft.displayName,
    serviceNumber: draft.serviceNumber.trim(),
    dialIn,
    pinRaw: draft.pinRaw,
    lockoutRisk: draft.lockoutRisk,
    implicitSave: draft.implicitSave,
  };
  const save = useMutation({
    mutationFn: () =>
      existing ? hybridApi.updateProvider(existing.key, body) : hybridApi.createProvider(body),
    onSuccess: onSaved,
  });
  return (
    <Dialog open onClose={onClose} title={existing ? "Provider bearbeiten" : "Neuer Provider"}>
      <div className="flex flex-col gap-4">
        <Field label="Schlüssel" hint="2 bis 32 Zeichen aus a-z, 0-9, _ und -; so heißt der Plan.">
          <Input
            value={draft.key}
            disabled={!!existing}
            onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase() })}
            placeholder="z. B. spusu"
          />
        </Field>
        <Field label="Anzeigename">
          <Input
            value={draft.displayName}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
          />
        </Field>
        <Field label="Servicenummer" hint="Nummer der Mobilbox-Zentrale; leer bei Direktwahl.">
          <Input
            value={draft.serviceNumber}
            onChange={(e) => setDraft({ ...draft, serviceNumber: e.target.value })}
            placeholder="z. B. 06762200"
          />
        </Field>
        <Field label="Direktwahl-Präfixe" hint="Nur bei Direktwahl: Vorwahl=Einwahlziffern, z. B. 0677=60, 0664=77.">
          <Input
            value={draft.dialIn}
            onChange={(e) => setDraft({ ...draft, dialIn: e.target.value })}
            placeholder="0677=60, 0664=77"
          />
        </Field>
        <Checkbox
          label="PIN ohne Raute"
          hint="Die Box nimmt die PIN ohne abschließende Raute."
          checked={draft.pinRaw}
          onChange={(e) => setDraft({ ...draft, pinRaw: e.target.checked })}
        />
        <Checkbox
          label="Sperrgefahr bei falscher PIN"
          hint="Der Roboter gibt die PIN dann höchstens einmal ein."
          checked={draft.lockoutRisk}
          onChange={(e) => setDraft({ ...draft, lockoutRisk: e.target.checked })}
        />
        <Checkbox
          label="Speichert ohne Bestätigung"
          hint="Die Box übernimmt die Aufnahme mit der Raute und sagt nichts dazu (Hot). Ohne diesen Haken verlangt der Roboter eine Speichern-Taste und eine Bestätigung."
          checked={draft.implicitSave}
          onChange={(e) => setDraft({ ...draft, implicitSave: e.target.checked })}
        />
        {noNumber && <ErrorNote>Eine Servicenummer oder mindestens ein Direktwahl-Präfix ist nötig.</ErrorNote>}
        {save.error && <ErrorNote>{(save.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={save.isPending || noNumber} onClick={() => save.mutate()}>
            {existing ? "Speichern" : "Anlegen"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function pretty(plan: unknown): string {
  return JSON.stringify(plan, null, 2);
}

function PlanView({
  provider,
  version,
  loading,
  previous,
  onEdit,
  onSnippet,
}: {
  provider: string;
  version: HybridPlanVersion | undefined;
  loading: boolean;
  previous: number;
  onEdit: (text: string) => void;
  onSnippet: (id: string) => void;
}) {
  const [view, setView] = useState<"tree" | "json" | "diff">("tree");
  const showDiff = view === "diff";
  const prev = useQuery({
    queryKey: ["hybrid-plan", provider, previous],
    queryFn: () => hybridApi.version(provider, previous),
    enabled: previous > 0 && showDiff,
  });

  if (loading || !version) return <Spinner />;
  const text = pretty(version.plan);

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            Plan v{version.version}{" "}
            <Badge tone={sourceTone[version.source] ?? "neutral"}>{sourceLabel[version.source] ?? version.source}</Badge>
            {version.active && (
              <span className="ml-1">
                <Badge tone="success">aktiv</Badge>
              </span>
            )}
          </h2>
          <p className="text-sm muted">
            {Object.keys(version.plan?.states ?? {}).length} Zustände ·{" "}
            {Object.keys(version.plan?.snippets ?? {}).length} Schnipsel
            {version.note && ` · ${version.note}`}
          </p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant={view === "tree" ? "primary" : undefined} onClick={() => setView("tree")}>
            Ablauf
          </Button>
          <Button size="sm" variant={view === "json" ? "primary" : undefined} onClick={() => setView("json")}>
            JSON
          </Button>
          {previous > 0 && (
            <Button size="sm" variant={view === "diff" ? "primary" : undefined} onClick={() => setView("diff")}>
              Unterschied zu v{previous}
            </Button>
          )}
        </div>
        <Button size="sm" onClick={() => onEdit(text)}>
          In den Editor laden
        </Button>
      </div>
      {view === "diff" && previous > 0 ? (
        prev.isLoading || !prev.data ? (
          <Spinner />
        ) : (
          <PlanDiff from={pretty(prev.data.plan)} to={text} fromLabel={`v${previous}`} toLabel={`v${version.version}`} />
        )
      ) : view === "tree" && version.plan ? (
        <PlanTree plan={version.plan} onSnippet={onSnippet} />
      ) : (
        <pre className="max-h-[60vh] overflow-auto px-4 py-3 font-mono text-xs leading-5">{text}</pre>
      )}
    </Card>
  );
}

function PlanDiff({
  from,
  to,
  fromLabel,
  toLabel,
}: {
  from: string;
  to: string;
  fromLabel: string;
  toLabel: string;
}) {
  const lines = useMemo(() => foldDiff(diffLines(from, to)), [from, to]);
  const changed = lines.filter((l) => l.kind === "added" || l.kind === "removed").length;
  return (
    <div>
      <p className="px-4 pt-2 text-xs muted">
        {fromLabel} → {toLabel}: {changed === 0 ? "kein Unterschied" : `${changed} geänderte Zeilen`}
      </p>
      <pre className="max-h-[60vh] overflow-auto px-4 py-3 font-mono text-xs leading-5">
        {lines.map((l, i) =>
          l.kind === "fold" ? (
            <div key={i} className="muted select-none">
              … {l.count} unveränderte Zeilen …
            </div>
          ) : (
            <div
              key={i}
              className={
                l.kind === "added"
                  ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-100"
                  : l.kind === "removed"
                    ? "bg-red-100 text-red-900 dark:bg-red-500/20 dark:text-red-100"
                    : ""
              }
            >
              <span className="inline-block w-4 select-none muted">
                {l.kind === "added" ? "+" : l.kind === "removed" ? "−" : " "}
              </span>
              {l.text}
            </div>
          ),
        )}
      </pre>
    </div>
  );
}

export function validationFields(err: unknown): Record<string, string> | null {
  if (!(err instanceof ApiError)) return null;
  if (err.fields && Object.keys(err.fields).length > 0) return err.fields;
  const body = err.body as { details?: Record<string, string> } | undefined;
  if (body?.details && typeof body.details === "object") return body.details;
  return null;
}

function PlanEditor({
  provider,
  seed,
  onSaved,
}: {
  provider: string;
  seed: { version: number; text: string } | null;
  onSaved: (v: HybridPlanVersion) => void;
}) {
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [loadedFrom, setLoadedFrom] = useState(0);
  const [checked, setChecked] = useState<null | { ok: true } | { ok: false; fields: Record<string, string>; detail: string }>(null);

  useEffect(() => {
    if (seed) {
      setText(seed.text);
      setLoadedFrom(seed.version);
      setChecked(null);
    }
  }, [seed]);
  useEffect(() => {
    setText("");
    setNote("");
    setLoadedFrom(0);
    setChecked(null);
  }, [provider]);

  const parse = (): unknown => {
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`Kein gültiges JSON: ${(e as Error).message}`);
    }
  };

  const validate = useMutation({
    mutationFn: () => hybridApi.validate(parse()),
    onSuccess: () => setChecked({ ok: true }),
    onError: (err) =>
      setChecked({ ok: false, fields: validationFields(err) ?? {}, detail: (err as Error).message }),
  });

  const save = useMutation({
    mutationFn: () => hybridApi.savePlan(provider, { plan: parse(), note }),
    onSuccess: (v) => {
      setChecked(null);
      setNote("");
      setLoadedFrom(v.version);
      onSaved(v);
    },
  });
  const saveFields = validationFields(save.error);

  return (
    <Card>
      <div className="border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
        <h2 className="text-base font-semibold">Plan bearbeiten</h2>
        <p className="text-sm muted">
          Speichern legt eine neue Version an; sie wird aktiv, wenn es noch keine aktive gibt,
          sonst aktivierst du sie oben.
          {loadedFrom > 0 && ` Geladen aus v${loadedFrom}.`}
        </p>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <Textarea
          className="min-h-96 font-mono text-xs leading-5"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setChecked(null);
          }}
          spellCheck={false}
          placeholder='{"schema": 1, "provider": "…", …}  — oder oben eine Version "In den Editor laden"'
        />
        {checked?.ok === true && <Notice tone="success">Der Plan ist gültig.</Notice>}
        {checked && checked.ok === false && (
          <ErrorNote>
            <div>{checked.detail}</div>
            {Object.keys(checked.fields).length > 0 && (
              <ul className="mt-1 list-disc pl-4">
                {Object.entries(checked.fields).map(([f, m]) => (
                  <li key={f}>
                    <span className="font-mono">{f}</span>: {m}
                  </li>
                ))}
              </ul>
            )}
          </ErrorNote>
        )}
        {save.error && (
          <ErrorNote>
            <div>{(save.error as Error).message}</div>
            {saveFields && (
              <ul className="mt-1 list-disc pl-4">
                {Object.entries(saveFields).map(([f, m]) => (
                  <li key={f}>
                    <span className="font-mono">{f}</span>: {m}
                  </li>
                ))}
              </ul>
            )}
          </ErrorNote>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-64 flex-1">
            <Field label="Notiz zur Version">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Was geändert wurde und warum" />
            </Field>
          </div>
          <Button onClick={() => validate.mutate()} disabled={!text.trim() || validate.isPending}>
            Prüfen
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={!text.trim() || save.isPending}>
            Als neue Version speichern
          </Button>
        </div>
      </div>
    </Card>
  );
}

// --- snippets ----------------------------------------------------------------

type SnippetRow = {
  id: string;
  meaning: string;
  pattern: string;
  audio: string;
  usedIn: string[];
  source: string;
  flags: string[];
  head: string;
};

// How a transition keys: the recording step waits for the tone; elsewhere
// the key test found out whether the box takes keys while it talks.
function keyTiming(t: HybridTransition): string {
  if (t.hold) return "Ton abwarten";
  if (!(t.do ?? []).some((a) => a.keys)) return "";
  if (t.barge_in === "ok") return "frühe Taste";
  if (t.barge_in === "no") return "nicht schneller: Ansage abwarten";
  return "frühe Taste wird im nächsten Lauf probiert";
}

// headLabel is how soon the robot knows a prompt by its sound alone.
function headLabel(s: HybridSnippet): string {
  if (!s.audio) return "—";
  if (!s.head_ms) return "noch nicht vermessen";
  if (s.head_ms < 0) return "nicht eindeutig";
  return `${(s.head_ms / 1000).toFixed(1)} s`;
}

function describeTransition(t: HybridTransition): string {
  if (t.outcome) return `→ ${outcomeLabel(t.outcome)}`;
  if (t.policy) return `→ ${t.policy}`;
  const keys = (t.do ?? [])
    .map((a) =>
      a.keys
        ? a.keys
        : a.wait_beep
          ? "Signalton"
          : a.play_greeting
            ? "Ansage"
            : a.settle
              ? "ausklingen"
              : a.hangup
                ? "auflegen"
                : a.wait_ms
                  ? `${a.wait_ms} ms`
                  : "",
    )
    .filter(Boolean)
    .join(", ");
  return `${describeCond(t)}${keys ? `[${keys}] ` : ""}→ ${t.next ?? "?"}${describeUse(t)}`;
}

const handoffReasons: Record<string, string> = {
  explore: "noch kein Plan — das Sprachmodell steuert",
  unmatched: "keine bekannte Ansage (weder Klang noch Wortlaut)",
  unsure: "das Schnellmodell war unsicher (alt)",
  reader_unavailable: "Schnellmodell nicht erreichbar (alt)",
  option_unsure: "das Schnellmodell war bei der Taste unsicher (alt)",
  no_options: "die Box nennt keine Taste",
  dangerous_option: "die Option wäre verboten",
  record_unconfirmed: "Aufsprechen nicht bestätigt",
  record_before_pin: "Aufsprechen vor der PIN",
  number_again: "die Box fragt erneut nach der Nummer",
  repeated: "die Box wiederholt sich",
  forbidden_key: "die Taste des Plans wäre hier verboten",
  visits: "Zustand zu oft besucht",
  beep_missing: "kein Signalton",
  reply_timeout: "keine Antwort der Box",
  premature_success: "noch nicht gespeichert",
  key_not_taken: "Taste nicht angenommen",
  guard: "ein Schutz hat die Wahl verworfen",
};

export function handoffReason(r: string): string {
  if (r.startsWith("policy:")) return `Regel des Plans (${r.slice(7)}) — das Sprachmodell prüft`;
  if (r.startsWith("confirm:")) return `Ergebnis (${r.slice(8)}) — das Sprachmodell bestätigt`;
  if (r.startsWith("verdict:")) return `Urteil (${r.slice(8)}) — das Sprachmodell prüft`;
  if (r.startsWith("s1:")) return `das Schnellmodell las „${r.slice(3)}“ (alt) — das beurteilte das Sprachmodell`;
  return handoffReasons[r] ?? r;
}

/** The condition of a branch, as "wenn …: ". */
function describeCond(t: HybridTransition): string {
  const c = t.when?.if;
  const parts: string[] = [];
  if (t.when?.else) parts.push("sonst");
  if (c) {
    const flag = (v: boolean | undefined, yes: string, no: string) => {
      if (v === true) parts.push(yes);
      if (v === false) parts.push(no);
    };
    flag(c.pin_entered, "PIN eingegeben", "vor der PIN");
    flag(c.greeting_played, "nach dem Aufsprechen", "vor dem Aufsprechen");
    flag(c.save_key_pressed, "nach dem Speichern", "vor dem Speichern");
    if (c.min_visits) parts.push(`ab ${c.min_visits}. Mal`);
    if (c.max_visits) parts.push(`bis ${c.max_visits}. Mal`);
  }
  return parts.length ? `wenn ${parts.join(", ")}: ` : "";
}

/** How much a branch is used, and whether it is switched off. */
function describeUse(t: HybridTransition): string {
  const p = t.provenance;
  if (!p) return "";
  const bits: string[] = [];
  if (p.uses) bits.push(`${p.uses}× genutzt`);
  if (p.last_used) bits.push(`zuletzt ${dateTime(p.last_used)}`);
  if (p.failures) bits.push(`${p.failures} Fehlschlag`);
  if (p.disabled) bits.push("abgeschaltet");
  return bits.length ? ` (${bits.join(", ")})` : "";
}

function snippetRows(plan: HybridPlan): SnippetRow[] {
  const usage = new Map<string, string[]>();
  const note = (id: string, where: string) => usage.set(id, [...(usage.get(id) ?? []), where]);
  for (const [state, s] of Object.entries(plan.states ?? {})) {
    for (const t of s.transitions ?? []) {
      const timing = keyTiming(t);
      if (t.when?.snippet) note(t.when.snippet, `${state} ${describeTransition(t)}${timing ? ` · ${timing}` : ""}`);
    }
  }
  for (const t of plan.global ?? []) {
    if (t.when?.snippet) note(t.when.snippet, `überall ${describeTransition(t)}`);
  }
  return Object.entries(plan.snippets ?? {})
    .map(([id, s]) => {
      const flags: string[] = [];
      if (s.provenance?.candidate) flags.push("Kandidat");
      if (s.provenance?.disabled) flags.push("abgeschaltet");
      return {
        id,
        meaning: s.meaning,
        pattern: s.description ?? "",
        audio: s.audio ? (s.variable ? "Aufnahme (wechselnder Inhalt)" : "Aufnahme") : "—",
        usedIn: usage.get(id) ?? [],
        source: s.provenance?.learned_from
          ? `gelernt (${s.provenance.by ?? "?"}, ${s.provenance.learned_from})`
          : "von Hand",
        flags,
        head: headLabel(s),
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
}

function SnippetList({ plan, onSnippet }: { plan: HybridPlan; onSnippet: (id: string) => void }) {
  const [filter, setFilter] = useState("");
  const rows = useMemo(() => snippetRows(plan), [plan]);
  const shown = rows.filter((r) => {
    const q = filter.trim().toLowerCase();
    return !q || [r.id, r.meaning, r.pattern, r.usedIn.join(" "), r.source].join(" ").toLowerCase().includes(q);
  });
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">Schnipsel</h2>
          <p className="text-sm muted">
            Die Ansagen dieses Anbieters, die der Plan kennt: ihre Aufnahme und ihre Wortlaute. Eine
            Ansage mit vermessenem Anfang erkennt der Roboter an ihrem Klang, ohne Whisper; sonst an
            ihren gelernten Wortlauten (die Wörter einer früheren Hörung, fast vollständig und in Reihenfolge
            wieder gehört); erst dann fragt er das Schnellmodell. Jeder erfolgreiche Anruf trägt neue Wortlaute
            nach. Ein Klick auf einen Schnipsel zeigt ihn mit Aufnahme und Wortlauten.
          </p>
        </div>
        <div className="w-64">
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filtern …" />
        </div>
      </div>
      <Table dense>
        <thead>
          <tr>
            <Th>Schnipsel</Th>
            <Th>Bedeutung</Th>
            <Th>Beschreibung</Th>
            <Th>Klang</Th>
            <Th>Anfang</Th>
            <Th>Verwendet in</Th>
            <Th>Quelle</Th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.id}>
              <Td className="font-mono text-xs">
                <button
                  type="button"
                  className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
                  onClick={() => onSnippet(r.id)}
                >
                  {r.id}
                </button>
                {r.flags.map((f) => (
                  <span key={f} className="ml-1">
                    <Badge tone={f === "abgeschaltet" ? "danger" : "warning"}>{f}</Badge>
                  </span>
                ))}
              </Td>
              <Td className="text-xs">{r.meaning}</Td>
              <Td className="max-w-md text-xs" title={r.pattern}>
                {r.pattern || <span className="muted">—</span>}
              </Td>
              <Td className="text-xs muted whitespace-nowrap">{r.audio}</Td>
              <Td className="text-xs whitespace-nowrap">{r.head}</Td>
              <Td className="text-xs">
                {r.usedIn.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  r.usedIn.map((u, i) => (
                    <div key={i} className="font-mono">
                      {u}
                    </div>
                  ))
                )}
              </Td>
              <Td className="text-xs muted">{r.source}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {shown.length === 0 && <Empty title="Kein Schnipsel" />}
    </Card>
  );
}

// --- the robot's switch ----------------------------------------------------

// Whether the robot knows prompts by the snippets' sound first, or has
// systemone read every one: for every call, unless a console call chooses
// for itself.
function RobotSettingsCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["hybrid-settings"], queryFn: hybridApi.settings });
  const save = useMutation({
    mutationFn: (snippets: boolean) => hybridApi.updateSettings({ snippets }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["hybrid-settings"] }),
  });
  if (!settings.data) return null;
  const on = settings.data.snippets;
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            Erkennung{" "}
            <Badge tone={on ? "success" : "info"}>{on ? "Klang, dann Wortlaut, dann Sprachmodell" : "ohne Klang: Wortlaut, dann Sprachmodell"}</Badge>
          </h2>
          <p className="text-sm muted">
            {on
              ? "Bekannte Ansagen erkennt der Roboter am Klang ihres Anfangs, ohne Whisper, sonst an ihrem gelernten Wortlaut; was der Plan nicht kennt, entscheidet das Sprachmodell. Gilt für alle Anrufe, außer ein Konsolen-Anruf wählt selbst."
              : "Der Roboter erkennt keine Ansage am Klang: nur die gelernten Wortlaute und das Sprachmodell entscheiden. Die Aufnahmen werden weiter gelernt, Zurückschalten geht ohne neu zu lernen."}
            {settings.data.updatedAt && ` Zuletzt geändert ${dateTime(settings.data.updatedAt)}.`}
          </p>
        </div>
        {canEdit && (
          <Button disabled={save.isPending} onClick={() => save.mutate(!on)}>
            {on ? "Klang abschalten" : "Klang wieder zuerst"}
          </Button>
        )}
      </div>
      {save.error && (
        <div className="mt-2">
          <ErrorNote>{(save.error as Error).message}</ErrorNote>
        </div>
      )}
    </Card>
  );
}

// --- the playbook as a tree ------------------------------------------------

function actionsLabel(t: HybridTransition): string {
  return (t.do ?? [])
    .map((a) =>
      a.keys
        ? `Taste ${a.keys.replace("$NUMBER#", "Nummer#").replace("$PIN#", "PIN#").replace("$PIN", "PIN")}`
        : a.wait_beep
          ? "Signalton abwarten"
          : a.play_greeting
            ? "Ansage aufsprechen"
            : a.settle
              ? "ausklingen lassen"
              : a.hangup
                ? "auflegen"
                : a.wait_ms
                  ? `${a.wait_ms} ms warten`
                  : "",
    )
    .filter(Boolean)
    .join(", ");
}

/** The states in the order a call meets them: breadth-first from the start. */
function stateOrder(plan: HybridPlan): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const queue = [plan.start];
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id) || !plan.states?.[id]) continue;
    seen.add(id);
    out.push(id);
    for (const t of plan.states[id].transitions ?? []) if (t.next && !seen.has(t.next)) queue.push(t.next);
  }
  return [...out, ...Object.keys(plan.states ?? {}).filter((id) => !seen.has(id)).sort()];
}

export function PlanTree({ plan, onSnippet }: { plan: HybridPlan; onSnippet: (id: string) => void }) {
  const order = stateOrder(plan);
  const index = new Map(order.map((id, i) => [id, i]));
  const reachable = new Set<string>();
  {
    const q = [plan.start];
    while (q.length) {
      const id = q.shift()!;
      if (reachable.has(id) || !plan.states?.[id]) continue;
      reachable.add(id);
      for (const t of plan.states[id].transitions ?? []) if (t.next) q.push(t.next);
    }
  }
  const snippetButton = (id: string) => {
    const sn = plan.snippets?.[id];
    return (
      <button
        type="button"
        className="font-mono underline decoration-dotted underline-offset-2 hover:decoration-solid"
        title={sn?.description ?? ""}
        onClick={() => onSnippet(id)}
      >
        »{id}«
      </button>
    );
  };
  const trigger = (t: HybridTransition) =>
    t.when?.snippet ? (
      snippetButton(t.when.snippet)
    ) : t.when?.else ? (
      <span className="italic">sonst (was die Box hier auch sagt)</span>
    ) : t.when?.after_ms ? (
      <span className="italic">nach {t.when.after_ms} ms Ruhe</span>
    ) : (
      <span className="muted">?</span>
    );
  const target = (from: string, t: HybridTransition) => {
    if (t.outcome) return <Badge tone={t.outcome === "success" ? "success" : "danger"}>{outcomeLabel(t.outcome)}</Badge>;
    if (t.policy) return <Badge tone="warning">{t.policy === "retry_recording" ? "Aufnahme wiederholen" : t.policy}</Badge>;
    if (!t.next) return <span className="muted">?</span>;
    const back = (index.get(t.next) ?? 0) <= (index.get(from) ?? 0);
    return (
      <a href={`#state-${t.next}`} className="font-mono hover:underline">
        {back ? "↩ " : "→ "}
        {t.next}
      </a>
    );
  };
  return (
    <div className="flex flex-col gap-3 px-4 py-3 text-sm">
      {(plan.global ?? []).length > 0 && (
        <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--border)" }}>
          <div className="mb-1 font-medium">Überall (globale Erkenner)</div>
          {(plan.global ?? []).map((t, i) => (
            <div key={i} className={"flex flex-wrap items-center gap-2 " + (t.provenance?.disabled ? "opacity-50 line-through" : "")}>
              {trigger(t)} {target("", t)}
              <span className="text-xs muted">{describeUse(t)}</span>
            </div>
          ))}
        </div>
      )}
      {order.map((id, i) => {
        const st = plan.states[id];
        if (!st) return null;
        return (
          <div
            key={id}
            id={`state-${id}`}
            className={"rounded-md border px-3 py-2 " + (reachable.has(id) ? "" : "opacity-60")}
            style={{ borderColor: "var(--border)" }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-6 text-xs muted tabular-nums">{i + 1}.</span>
              <span className="font-mono font-medium">{id}</span>
              <Badge>{st.meaning}</Badge>
              {id === plan.start && <Badge tone="info">Start</Badge>}
              {!reachable.has(id) && <Badge tone="warning">vom Start nicht erreichbar</Badge>}
              {st.max_visits ? <span className="text-xs muted">höchstens {st.max_visits}× je Anruf</span> : null}
            </div>
            <ul className="mt-1 ml-8 flex flex-col gap-1">
              {(st.transitions ?? []).map((t, j) => {
                const timing = keyTiming(t);
                return (
                  <li
                    key={j}
                    className={"flex flex-wrap items-center gap-x-2 gap-y-0.5 " + (t.provenance?.disabled ? "opacity-50 line-through" : "")}
                  >
                    <span className="muted">{j === 0 ? "wenn" : "sonst wenn"}</span>
                    {describeCond(t) && <span className="text-xs">{describeCond(t).replace(/: $/, "")}</span>}
                    {trigger(t)}
                    {actionsLabel(t) && <span className="font-medium">{actionsLabel(t)}</span>}
                    {target(id, t)}
                    {t.hold && <Badge tone="info">bis zum Signalton</Badge>}
                    {timing && <span className="text-xs muted">{timing}</span>}
                    <span className="text-xs muted">
                      {describeUse(t)}
                      {t.provenance?.by && ` · gelernt von ${t.provenance.by === "s1" ? "Schnellmodell" : t.provenance.by === "llm" ? "Sprachmodell" : t.provenance.by}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// --- one snippet -------------------------------------------------------------

export function SnippetDialog({ plan, id, onClose }: { plan: HybridPlan; id: string; onClose: () => void }) {
  const sn = plan.snippets?.[id];
  if (!sn) return null;
  const url = sn.audio ? snippetAudioUrl(sn.audio) : null;
  const uses: { where: string; t: HybridTransition }[] = [];
  for (const [state, st] of Object.entries(plan.states ?? {}))
    for (const t of st.transitions ?? []) if (t.when?.snippet === id) uses.push({ where: state, t });
  for (const t of plan.global ?? []) if (t.when?.snippet === id) uses.push({ where: "überall", t });
  const p = sn.provenance;
  return (
    <Dialog open onClose={onClose} title={`Schnipsel ${id}`} size="lg">
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap gap-2">
          <Badge>{sn.meaning}</Badge>
          {sn.variable && <Badge tone="info">wechselnder Inhalt</Badge>}
          {p?.candidate && <Badge tone="warning">Kandidat</Badge>}
          {p?.disabled && <Badge tone="danger">abgeschaltet</Badge>}
        </div>
        <Field label="Beschreibung" hint="Wie die Ansage beim ersten Lernen gehört wurde.">
          <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--border)" }}>
            {sn.description || <span className="muted">—</span>}
          </div>
        </Field>
        <Field
          label="Wortlaute"
          hint="Jede Hörung der Ansage; der Roboter erkennt sie, wenn einer davon fast vollständig und in Reihenfolge wieder gehört wird."
        >
          {sn.wordings && sn.wordings.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {sn.wordings.map((w, i) => (
                <li key={i} className="rounded-md border px-3 py-2" style={{ borderColor: "var(--border)" }}>
                  <div>{w.text}</div>
                  <div className="mt-1 text-xs muted">
                    {w.learned_from ? `gelernt in Sitzung ${w.learned_from}` : "von Hand"}
                    {w.by && ` · von ${w.by === "s1" ? "Schnellmodell" : w.by === "llm" ? "Sprachmodell" : w.by === "plan" ? "Plan" : w.by}`}
                    {w.state && ` · Zustand ${w.state}`}
                    {w.at && ` · ${dateTime(w.at)}`}
                    {w.sightings ? ` · ${w.sightings}× gehört` : ""}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <span className="muted">—</span>
          )}
        </Field>
        <Field
          label="Aufnahme"
          hint={
            sn.audio
              ? `Am Anfang erkennbar: ${headLabel(sn)}. ${sn.variable ? "Erkannt an den ersten Sekunden, der Inhalt danach wechselt." : ""}`
              : "Noch keine Aufnahme: die Ansage wird nur über das Schnellmodell erkannt, bis ein Anruf sie aufnimmt."
          }
        >
          {url ? (
            <AudioPlayer src={url} label={sn.audio} />
          ) : sn.audio ? (
            <span className="font-mono text-xs muted">{sn.audio}</span>
          ) : (
            <span className="muted">—</span>
          )}
        </Field>
        <Field label="Verwendet in">
          {uses.length === 0 ? (
            <span className="muted">nirgends</span>
          ) : (
            <ul className="flex flex-col gap-1">
              {uses.map((u, i) => (
                <li key={i} className={u.t.provenance?.disabled ? "opacity-50 line-through" : ""}>
                  <span className="font-mono">{u.where}</span>: {actionsLabel(u.t) || "nichts drücken"}{" "}
                  {u.t.outcome ? `→ ${outcomeLabel(u.t.outcome)}` : u.t.next ? `→ ${u.t.next}` : u.t.policy ? `→ ${u.t.policy}` : ""}
                  <span className="text-xs muted">{describeUse(u.t)}</span>
                </li>
              ))}
            </ul>
          )}
        </Field>
        {p && (
          <Field label="Herkunft">
            <div className="text-xs muted">
              {p.learned_from ? `gelernt in Sitzung ${p.learned_from}` : "von Hand"}
              {p.by && ` · von ${p.by === "s1" ? "Schnellmodell" : p.by === "llm" ? "Sprachmodell" : p.by}`}
              {p.at && ` · ${dateTime(p.at)}`}
              {p.sightings ? ` · ${p.sightings}× gehört` : ""}
              {p.confidence ? ` · Konfidenz ${(p.confidence * 100).toFixed(0)} %` : ""}
            </div>
          </Field>
        )}
      </div>
    </Dialog>
  );
}

// The icon the navigation uses, re-exported so that main.tsx and layout.tsx
// name the same one.
export const HybridIcon = Bot;
